/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useMemo } from 'react';
import { usePlayerContext } from 'application/providers/PlayerProvider';
import AnalyticsService, { EventNameAMP, ParamNameAMP, StreamTypeValues } from 'application/services/AnalyticsService';
import { MediaFileTypes } from 'application/pages/types';
import FileUtils from 'application/utils/FileUtils';
import { SILENT_MEDIAFILE_ID } from 'application/consts';
import { MediaListItem } from '../MediaListItem';
import { MediaListProps } from '../types';
import useDislike from './useDislike.hook';
import MediaListItemIconsLike from '../MediaListItemIcons/MediaListItemIconsLike';
import { MediaListItemIconsDislike } from '../MediaListItemIcons';

interface MediaListItemRendererProps {
  mediafile: MediaListProps['mediafiles'][0];
  streamId: MediaListProps['streamId'];
  timezone: MediaListProps['timezone'];
  playtimeName: MediaListProps['playtimeName'];
  withLikes: MediaListProps['withLikes'];
  withDislikes: MediaListProps['withDislikes'];
  withPlaytime: MediaListProps['withPlaytime'];
  mediaListItemIcons?: MediaListProps['mediaListItemIcons'];
}

export const MediaListItemRenderer: FC<MediaListItemRendererProps> = (props) => {
  const {
    mediafile,
    streamId,
    timezone,
    playtimeName = 'playtime',
    withLikes = false,
    withDislikes = false,
    withPlaytime = false,
    mediaListItemIcons,
  } = props;
  const {
    isPaused,
    progress,
    fileName: playingMediafileName,
    fileUuid: playingMediafileUuid,
    play,
    pause,
    resume,
    changeProgress,
  } = usePlayerContext();

  const {
    id,
    title,
    performerTitle,
    type,
    explicit,
    mediafileIsDisliked,
    filename,
    mediafileId,
    isLiked,
    sourcefiles,
    isDeleting,
  } = mediafile;
  const fileId = id || mediafileId;
  const key = `mediafile_item_${filename}_${mediafileId}_${id}`;
  const playTime = withPlaytime ? mediafile[playtimeName] : undefined;
  const isMediafilePlaying = playingMediafileUuid
    ? playingMediafileUuid === mediafile.id && playingMediafileName === filename
    : playingMediafileName === filename;
  const currentProgress = isMediafilePlaying ? progress : undefined;

  const { isDisliked, onDislike } = useDislike({ id, streamId, mediafileIsDisliked });

  const mediaListItemChildrenProps = {
    id: mediafileId || id,
    title,
    type,
    streamId: mediafile.streamId || streamId,
    withLikes,
    withDislikes,
    isDisliked,
    onDislike,
    playTime,
    timezone,
    isLiked,
  };

  const playHandler = () => {
    if (isDeleting) return;

    const fileUrl = FileUtils.getMediafileUrl(sourcefiles);

    play({ fileName: filename, fileUuid: fileId, fileUrl });

    AnalyticsService.event(EventNameAMP.PLAY_TRACK_PRESSED, {
      [ParamNameAMP.STREAM_ID]: streamId,
      [ParamNameAMP.STREAM_TYPE]: StreamTypeValues.TEMPLATE,
      [ParamNameAMP.TRACK_ID]: fileId,
      [ParamNameAMP.IS_DEMO]: false,
      [ParamNameAMP.IS_PROMO]: type === MediaFileTypes.PROMO,
    });
  };

  const mediaListItemChildren = useMemo(
    () =>
      mediaListItemIcons?.map((Component, index) => {
        const TypedComponent = Component as React.FC;
        const isSilentTrack = mediafileId?.toString() === SILENT_MEDIAFILE_ID.toString();
        const isLikeOrDislike =
          TypedComponent === MediaListItemIconsLike || TypedComponent === MediaListItemIconsDislike;

        if ((isSilentTrack || isDeleting) && isLikeOrDislike) {
          return null;
        }

        return (
          <Component
            {...mediaListItemChildrenProps}
            // eslint-disable-next-line react/no-array-index-key
            key={`${mediaListItemChildrenProps.id}_${index}`}
          />
        );
      }),
    [mediaListItemIcons, mediaListItemChildrenProps]
  );

  return (
    <MediaListItem
      key={key}
      title={title}
      performerTitle={performerTitle}
      isMediafilePlaying={isMediafilePlaying}
      progress={currentProgress}
      type={type}
      isDisliked={isDisliked}
      onClickPlay={playHandler}
      onClickPause={pause}
      onClickResume={resume}
      onChangeProgress={changeProgress}
      isPaused={isPaused}
      explicit={explicit}
      disabled={isDeleting}>
      {mediaListItemChildren}
    </MediaListItem>
  );
};
